import React, { useState, useCallback, useRef } from 'react';
import { _ } from '@splunk/ui-utils/i18n';
import { useDashboardApi, useSearchContext } from '@splunk/dashboard-context';
import {
    exportDashboardFailure,
    exportDashboardSuccess,
    selectTabs,
    selectTitle,
    useDispatch,
    useSelector,
} from '@splunk/dashboard-state';
import { type returnFocus } from '../Modal';
import { ExportModalContent } from './ExportModalContent';
import { DEFAULT_RENDER_TIME } from './ExportConfigScreen';

interface ExportDashboardModalArgs {
    open: boolean;
    onModalClose: () => void;
    fileType?: 'pdf' | 'png';
    fileName?: string;
    layoutIds?: string[];
    renderTime?: number;
    returnFocus: returnFocus;
    expandAdvancedSection?: boolean;
    scale?: number;
    searchProgressInterval?: number;
}

const DEFAULT_TITLE = _('Dashboard title');

export const ExportDashboardModal = ({
    open,
    onModalClose,
    fileType: initialFileType = 'pdf',
    fileName: initialFileName,
    layoutIds: initialLayoutIds,
    renderTime: initialRenderTime,
    returnFocus,
    expandAdvancedSection = false,
    scale,
    searchProgressInterval = 1000,
}: ExportDashboardModalArgs) => {
    const [modalStep, setModalStep] = useState(0);
    const dashboardTitle = useSelector(selectTitle);
    const [fileType, setFileType] = useState(initialFileType);
    const [fileName, setFileName] = useState(
        initialFileName ?? dashboardTitle ?? DEFAULT_TITLE
    );
    const [renderTime, setRenderTime] = useState(
        initialRenderTime ?? DEFAULT_RENDER_TIME
    );
    const [isDownloadingTabs, setIsDownloadingTabs] = useState(false);
    const abortControllerRef = useRef<AbortController>();

    const tabs = useSelector(selectTabs);
    const isTabbedDashboard = tabs.length > 1;

    // give priority to the prop, otherwise default to all tabs selected
    const [layoutIds, setLayoutIds] = useState(() =>
        isTabbedDashboard
            ? (initialLayoutIds ?? tabs.map((tab) => tab.layoutId))
            : [tabs[0]?.layoutId]
    );

    const searchApi = useSearchContext();
    const dashboardApi = useDashboardApi();
    const dispatch = useDispatch();

    const handleModalClose = useCallback(() => {
        if (
            isDownloadingTabs &&
            modalStep === 2 &&
            abortControllerRef.current
        ) {
            // cancel the exportDashboardToFile promise
            abortControllerRef.current.abort();
        }

        // searches are still executing
        searchApi.setLayoutsToInactive({
            layoutIds,
        });

        setModalStep(0);
        setIsDownloadingTabs(false);
        onModalClose();
    }, [isDownloadingTabs, layoutIds, modalStep, onModalClose, searchApi]);

    const handleDownloadDashboard = useCallback(async () => {
        setIsDownloadingTabs(true);
        setModalStep(2);

        abortControllerRef.current = new AbortController();

        try {
            await dashboardApi.exportDashboardToFile({
                outputType: 'save',
                fileType,
                fileName,
                layoutIds,
                renderTime: renderTime * 1000,
                signal: abortControllerRef.current.signal,
                scale,
            });
            if (!abortControllerRef.current.signal.aborted) {
                dispatch(
                    exportDashboardSuccess({
                        source: 'toolbar',
                        fileType,
                        numTabsSelected: layoutIds.length,
                        numTabsTotal: tabs.length,
                        renderTime,
                    })
                );

                // only close modal if the previous async call wasn't aborted. Otherwise, the cancel
                //  button will have taken care of the modal closing
                handleModalClose();
            }
        } catch (err) {
            if (err instanceof Error) {
                dispatch(
                    exportDashboardFailure({
                        source: 'toolbar',
                        fileType,
                        numTabsSelected: layoutIds.length,
                        numTabsTotal: tabs.length,
                        renderTime,
                        error: err.message,
                    })
                );
            }
            handleModalClose();
        }
    }, [
        dashboardApi,
        dispatch,
        fileType,
        fileName,
        layoutIds,
        renderTime,
        scale,
        tabs.length,
        handleModalClose,
    ]);

    return (
        <ExportModalContent
            open={open}
            fileType={fileType}
            fileName={fileName}
            layoutIds={layoutIds}
            renderTime={renderTime}
            expandAdvancedSection={expandAdvancedSection}
            modalStep={modalStep}
            isTabbedDashboard={isTabbedDashboard}
            tabs={tabs}
            isDownloadingTabs={isDownloadingTabs}
            onFileNameChange={setFileName}
            onFileTypeChange={setFileType}
            onLayoutIdsChange={setLayoutIds}
            onRenderTimeChange={setRenderTime}
            onProgress={setModalStep}
            onDownloadDashboard={handleDownloadDashboard}
            onRequestClose={handleModalClose}
            returnFocus={returnFocus}
            searchProgressInterval={searchProgressInterval}
        />
    );
};
